// Abonelik, kullanım ve firma ayarları (C80–C87): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Şemada karşılığı olmayan ayarlar (vergi dairesi, logo, otomatik başlatma, anonimleştirme) CompanySettings.notificationConfig
// JSON'unda ad alanlarıyla tutulur: { notifications, profile, tripRules, privacy } — okuma/yazma `parseSettingsConfig` ile.
import { z } from 'zod';
import { PLAN_KEYS, PRODUCT_DOMAIN, type Module, type PlanKey, type TenantType } from '../enums';
import { HEX_COLOR, HEX_COLOR_MESSAGE, brandContrastError, type WhiteLabelPalette } from './white-label';

// ───────── abonelik (C80–C82) ─────────

export const SUBSCRIPTION_STATUSES = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELLED'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  TRIAL: 'Deneme sürümü',
  ACTIVE: 'Etkin',
  PAST_DUE: 'Ödeme gecikti',
  CANCELLED: 'İptal edildi',
};

export const USAGE_METRICS = [
  'VEHICLES',
  'PASSENGERS',
  'SMS',
  'API_CALLS',
  'MAP_LOADS',
  'DIRECTIONS',
  'GEOCODING',
  'AI_RUNS',
  'STORAGE_MB',
] as const;
export type UsageMetric = (typeof USAGE_METRICS)[number];
export const USAGE_METRIC_LABELS: Record<UsageMetric, string> = {
  VEHICLES: 'Araç',
  PASSENGERS: 'Yolcu',
  SMS: 'SMS',
  API_CALLS: 'API çağrısı',
  MAP_LOADS: 'Harita yükleme',
  DIRECTIONS: 'Rota hesaplama',
  GEOCODING: 'Adres arama',
  AI_RUNS: 'AI optimizasyon',
  STORAGE_MB: 'Depolama',
};
/** Ölçüm birimi (sayılabilen adetlerde boş). */
export const USAGE_METRIC_UNITS: Partial<Record<UsageMetric, string>> = { STORAGE_MB: 'MB' };
/** Ölçümün görünmesi için gereken modül (kapalıysa hiç gösterilmez). */
export const USAGE_METRIC_FLAGS: Partial<Record<UsageMetric, Module>> = {
  API_CALLS: 'API',
  AI_RUNS: 'AI_OPTIMIZATION',
};
/** Plan sınırı olan ölçümler (C80 "dahil olanlar", C81 ilerleme çubukları). */
export const LIMITED_METRICS = ['VEHICLES', 'PASSENGERS', 'SMS', 'API_CALLS'] as const;
export type LimitedMetric = (typeof LIMITED_METRICS)[number];

/** %80 ve üstü sarı (yaklaşıyor), %100 ve üstü kırmızı (aşıldı). */
export const USAGE_WARN_PCT = 80;
export type UsageLevel = 'ok' | 'warn' | 'over' | 'unlimited';
export const USAGE_LEVEL_LABELS: Record<UsageLevel, string> = {
  ok: 'Sınırın altında',
  warn: 'Sınıra yaklaştı',
  over: 'Sınır aşıldı',
  unlimited: 'Sınırsız',
};

export function usageLevel(quantity: number, limit: number | null): { pct: number | null; level: UsageLevel } {
  if (limit === null || limit <= 0) return { pct: null, level: 'unlimited' };
  const pct = Math.round((quantity / limit) * 1000) / 10;
  return { pct, level: pct >= 100 ? 'over' : pct >= USAGE_WARN_PCT ? 'warn' : 'ok' };
}

/** API çağrısı kotası (aylık): planın `features.apiCallQuota` değeri yoksa plan anahtarına göre. Enterprise sınırsız. */
export const DEFAULT_API_CALL_QUOTA: Record<PlanKey, number | null> = {
  STARTER: 10_000,
  GROWTH: 50_000,
  BUSINESS: 250_000,
  ENTERPRISE: null,
};

export interface PlanLimits {
  vehicles: number | null;
  passengers: number | null;
  sms: number | null;
  apiCalls: number | null;
}

export interface PlanFeatures {
  liveTracking: boolean;
  notifications: boolean;
  qr: boolean;
  reports: 'basic' | 'advanced';
}

export interface PlanItem {
  id: string;
  key: PlanKey;
  name: string;
  /** aylık fiyat (kuruş); `customPrice` ise teklifle belirlenir */
  monthlyPrice: number;
  customPrice: boolean;
  limits: PlanLimits;
  modules: Module[];
  features: PlanFeatures;
  current: boolean;
  rank: number;
}

export interface SubscriptionModule {
  module: Module;
  label: string;
  enabled: boolean;
  inPlan: boolean;
}

export interface UpgradeRequestInfo {
  planKey: PlanKey;
  planName: string;
  ticketId: string;
  ticketNumber: string;
  status: 'PENDING';
  requestedAt: string;
  requestedByName: string | null;
  note: string | null;
}

export interface UsageItem {
  metric: UsageMetric;
  label: string;
  unit: string | null;
  quantity: number;
  limit: number | null;
  pct: number | null;
  level: UsageLevel;
}

export interface SubscriptionOverview {
  plan: PlanItem;
  status: SubscriptionStatus;
  statusLabel: string;
  /** aylık tutar (kuruş) */
  mrr: number;
  periodStart: string;
  periodEnd: string;
  /** yenileme tarihi (dönem sonu); iptalde null */
  renewsAt: string | null;
  trialEndsAt: string | null;
  daysLeft: number;
  /** sözleşmeye özel araç sınırı (Enterprise) */
  vehicleLimitOverride: number | null;
  modules: SubscriptionModule[];
  usage: UsageItem[];
  usagePeriod: string | null;
  upgradeRequest: UpgradeRequestInfo | null;
  /** bir üst plan (yoksa en üst plandadır) */
  nextPlanKey: PlanKey | null;
}

export interface UsageTrendPoint {
  date: string;
  /** gün içinde sefere çıkan araç */
  vehicles: number;
  /** başlayan / tamamlanan sefer */
  trips: number;
  /** biniş olayı */
  boardings: number;
}

export interface UsageReport {
  period: string;
  periods: string[];
  current: boolean;
  limits: PlanLimits;
  items: UsageItem[];
  /** sınırı olmayan ölçümler (bilgi) */
  other: UsageItem[];
  trend: UsageTrendPoint[];
}

export const UsageQuery = z.object({
  period: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-AA biçiminde olmalı')
    .optional(),
});
export type UsageQuery = z.infer<typeof UsageQuery>;

export interface PlanCatalog {
  items: PlanItem[];
  currentKey: PlanKey;
  upgradeRequest: UpgradeRequestInfo | null;
}

export const UpgradeRequestInput = z.object({
  planKey: z.enum(PLAN_KEYS),
  note: z.string().trim().max(500, 'Not en fazla 500 karakter olabilir').optional(),
});
export type UpgradeRequestInput = z.infer<typeof UpgradeRequestInput>;

export interface UpgradeRequestResult {
  ticketNumber: string;
  upgradeRequest: UpgradeRequestInfo;
  message: string;
}

/** Plan karşılaştırma satırları (C82) — modül satırları MODULE_LABELS'tan. */
export const PLAN_FEATURE_LABELS: Record<keyof PlanFeatures, string> = {
  liveTracking: 'Canlı takip ve ETA',
  notifications: 'Yolcu bildirimleri',
  qr: 'QR / PIN ile biniş',
  reports: 'Raporlar',
};

// ───────── firma profili (C83) ─────────

export const ACCOUNT_TIMEZONE = 'Europe/Istanbul';

export interface CompanyProfile {
  id: string;
  type: TenantType;
  name: string;
  legalName: string | null;
  taxOffice: string | null;
  taxNo: string | null;
  address: string | null;
  city: string;
  phone: string | null;
  email: string | null;
  logoFileId: string | null;
  logoUrl: string | null;
  timezone: string;
  updatedAt: string;
}

const optText = (max: number, msg: string) => z.string().trim().max(max, msg).nullable().optional();

export const CompanyProfileInput = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Firma adı en az 2 karakter olmalı')
    .max(80, 'Firma adı en fazla 80 karakter olabilir'),
  legalName: optText(160, 'Ticari unvan en fazla 160 karakter olabilir'),
  taxOffice: optText(60, 'Vergi dairesi en fazla 60 karakter olabilir'),
  taxNo: z
    .string()
    .trim()
    .regex(/^(\d{10}|\d{11})$/, 'Vergi numarası 10, T.C. kimlik numarası 11 haneli olmalı')
    .nullable()
    .optional(),
  address: optText(240, 'Adres en fazla 240 karakter olabilir'),
  city: z.string().trim().min(2, 'Şehir girin').max(40),
  phone: z
    .string()
    .regex(/^\+90\d{10}$/, 'Telefonu 10 haneli girin (ör. 212 000 00 00)')
    .nullable()
    .optional(),
  email: z.email('Geçerli bir e-posta girin').max(120).nullable().optional(),
  logoFileId: z.string().max(64).nullable().optional(),
});
export type CompanyProfileInput = z.infer<typeof CompanyProfileInput>;

// ───────── marka / beyaz etiket (C84) ─────────

/** Beyaz etiket alan adının yönleneceği CNAME hedefi. */
export const WHITE_LABEL_CNAME = `cname.${PRODUCT_DOMAIN}`;

export const DOMAIN_STATUSES = ['PENDING', 'VERIFIED', 'FAILED'] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];
export const DOMAIN_STATUS_LABELS: Record<DomainStatus, string> = {
  PENDING: 'Doğrulanmadı',
  VERIFIED: 'Doğrulandı',
  FAILED: 'Doğrulanamadı',
};

export interface BrandingSettings {
  /** kayıt yoksa SEROTA varsayılanları döner */
  configured: boolean;
  companyName: string;
  appName: string | null;
  logoFileId: string | null;
  logoUrl: string | null;
  brand: string;
  palette: WhiteLabelPalette;
  contrast: number;
  customDomain: string | null;
  domainStatus: DomainStatus;
  domainStatusLabel: string;
  cnameTarget: string;
  updatedAt: string | null;
}

const HOSTNAME = /^(?=.{4,253}$)([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;

export const BrandingInput = z
  .object({
    appName: z
      .string()
      .trim()
      .min(2, 'Uygulama adı en az 2 karakter olmalı')
      .max(30, 'Uygulama adı en fazla 30 karakter olabilir'),
    brand: z.string().regex(HEX_COLOR, HEX_COLOR_MESSAGE),
    logoFileId: z.string().max(64).nullable().optional(),
    customDomain: z
      .string()
      .trim()
      .regex(HOSTNAME, 'Alan adını servis.firmaniz.com.tr biçiminde girin')
      .nullable()
      .optional(),
  })
  .superRefine((v, ctx) => {
    const err = brandContrastError(v.brand);
    if (err) ctx.addIssue({ code: 'custom', path: ['brand'], message: err });
  });
export type BrandingInput = z.infer<typeof BrandingInput>;

export interface DomainVerifyResult {
  domainStatus: DomainStatus;
  domainStatusLabel: string;
  message: string;
}

// ───────── bildirim ayarları (C85) ─────────

export const SETTINGS_EVENTS = [
  'TRIP_STARTED',
  'APPROACHING',
  'DELAY',
  'VEHICLE_CHANGED',
  'DRIVER_CHANGED',
  'TRIP_CANCELLED',
  'CRITICAL_ALERT',
  'DOC_EXPIRING',
  'CHILD_EVENTS',
] as const;
export type SettingsEvent = (typeof SETTINGS_EVENTS)[number];

export const SETTINGS_RECIPIENTS = ['PASSENGERS', 'GUARDIANS', 'INSTITUTION', 'OPERATIONS', 'DRIVER'] as const;
export type SettingsRecipient = (typeof SETTINGS_RECIPIENTS)[number];
export const SETTINGS_RECIPIENT_LABELS: Record<SettingsRecipient, string> = {
  PASSENGERS: 'Yolcu',
  GUARDIANS: 'Veli',
  INSTITUTION: 'Kurum yetkilisi',
  OPERATIONS: 'Operasyon ekibi',
  DRIVER: 'Şoför',
};
/** Veli alıcısı yalnız Okul modülüyle görünür. */
export const SETTINGS_RECIPIENT_FLAGS: Partial<Record<SettingsRecipient, Module>> = { GUARDIANS: 'SCHOOL' };

export const SETTINGS_CHANNELS = ['PUSH', 'SMS', 'EMAIL'] as const;
export type SettingsChannel = (typeof SETTINGS_CHANNELS)[number];
export const SETTINGS_CHANNEL_LABELS: Record<SettingsChannel, string> = {
  PUSH: 'Push',
  SMS: 'SMS',
  EMAIL: 'E-posta',
};

export interface SettingsEventMeta {
  label: string;
  description: string;
  allowed: SettingsRecipient[];
  recipients: SettingsRecipient[];
  channels: SettingsChannel[];
  /** çocuk güvenliği: kapatılamaz, veli alıcısı çıkarılamaz */
  safety?: boolean;
  flag?: Module;
}

export const SETTINGS_EVENT_META: Record<SettingsEvent, SettingsEventMeta> = {
  TRIP_STARTED: {
    label: 'Sefer başladı',
    description: 'Şoför sefere başladığında',
    allowed: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION'],
    recipients: ['PASSENGERS', 'GUARDIANS'],
    channels: ['PUSH'],
  },
  APPROACHING: {
    label: 'Servis yaklaşıyor',
    description: 'Servis durağa yaklaştığında (aşağıdaki dakika)',
    allowed: ['PASSENGERS', 'GUARDIANS'],
    recipients: ['PASSENGERS', 'GUARDIANS'],
    channels: ['PUSH'],
  },
  DELAY: {
    label: 'Gecikme',
    description: 'Gecikme bildirim eşiğini geçtiğinde',
    allowed: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION', 'OPERATIONS'],
    recipients: ['PASSENGERS', 'GUARDIANS', 'OPERATIONS'],
    channels: ['PUSH'],
  },
  VEHICLE_CHANGED: {
    label: 'Araç değişti',
    description: 'Sefere başka araç atandığında',
    allowed: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION'],
    recipients: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION'],
    channels: ['PUSH'],
  },
  DRIVER_CHANGED: {
    label: 'Şoför değişti',
    description: 'Sefere başka şoför atandığında',
    allowed: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION'],
    recipients: ['PASSENGERS', 'GUARDIANS'],
    channels: ['PUSH'],
  },
  TRIP_CANCELLED: {
    label: 'Sefer iptal edildi',
    description: 'Planlı sefer iptal edildiğinde',
    allowed: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION', 'DRIVER'],
    recipients: ['PASSENGERS', 'GUARDIANS', 'INSTITUTION', 'DRIVER'],
    channels: ['PUSH', 'SMS'],
  },
  CRITICAL_ALERT: {
    label: 'Kritik uyarı',
    description: 'Arıza, kaza veya uzun süreli GPS kaybı',
    allowed: ['OPERATIONS', 'INSTITUTION'],
    recipients: ['OPERATIONS'],
    channels: ['PUSH', 'SMS'],
  },
  DOC_EXPIRING: {
    label: 'Belge süresi doluyor',
    description: 'Sigorta, muayene, ruhsat veya ehliyet süresi yaklaştığında',
    allowed: ['OPERATIONS', 'DRIVER'],
    recipients: ['OPERATIONS'],
    channels: ['EMAIL'],
  },
  CHILD_EVENTS: {
    label: 'Çocuk bindi / indi / teslim edildi',
    description: 'Çocuk güvenliği bildirimi; kapatılamaz',
    allowed: ['GUARDIANS', 'INSTITUTION'],
    recipients: ['GUARDIANS'],
    channels: ['PUSH'],
    safety: true,
    flag: 'SCHOOL',
  },
};

export const APPROACH_MIN_OPTIONS = [2, 3, 5, 7, 10] as const;
export const DELAY_NOTIFY_OPTIONS = [5, 10, 15, 20] as const;
export const DOC_EXPIRY_OPTIONS = [7, 15, 30, 60] as const;
export const DIGEST_TIME_OPTIONS = ['07:00', '09:00', '18:00', '20:00'] as const;

export interface NotificationEventSetting {
  key: SettingsEvent;
  label: string;
  description: string;
  enabled: boolean;
  recipients: SettingsRecipient[];
  channels: SettingsChannel[];
  allowed: SettingsRecipient[];
  safety: boolean;
}

export interface NotificationSettings {
  events: NotificationEventSetting[];
  approachMin: number;
  delayNotifyMin: number;
  docExpiryDays: number;
  dailyDigest: { enabled: boolean; time: string };
  smsFallback: boolean;
  updatedAt: string | null;
}

export const NotificationSettingsInput = z
  .object({
    events: z
      .array(
        z.object({
          key: z.enum(SETTINGS_EVENTS),
          enabled: z.boolean(),
          recipients: z.array(z.enum(SETTINGS_RECIPIENTS)).max(SETTINGS_RECIPIENTS.length),
          channels: z.array(z.enum(SETTINGS_CHANNELS)).max(SETTINGS_CHANNELS.length),
        }),
      )
      .max(SETTINGS_EVENTS.length),
    approachMin: z
      .number()
      .int()
      .refine((v) => (APPROACH_MIN_OPTIONS as readonly number[]).includes(v), 'Yaklaşıyor süresini listeden seçin'),
    delayNotifyMin: z
      .number()
      .int()
      .refine((v) => (DELAY_NOTIFY_OPTIONS as readonly number[]).includes(v), 'Gecikme eşiğini listeden seçin'),
    docExpiryDays: z
      .number()
      .int()
      .refine((v) => (DOC_EXPIRY_OPTIONS as readonly number[]).includes(v), 'Belge uyarı süresini listeden seçin'),
    dailyDigest: z.object({
      enabled: z.boolean(),
      time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Saat SS:DD biçiminde olmalı'),
    }),
    smsFallback: z.boolean(),
  })
  .superRefine((v, ctx) => {
    v.events.forEach((e, i) => {
      if (!e.enabled) return;
      const label = SETTINGS_EVENT_META[e.key].label;
      if (!e.channels.length)
        ctx.addIssue({
          code: 'custom',
          path: ['events', i, 'channels'],
          message: `"${label}" için en az bir kanal seçin.`,
        });
      if (!e.recipients.length)
        ctx.addIssue({
          code: 'custom',
          path: ['events', i, 'recipients'],
          message: `"${label}" için en az bir alıcı seçin.`,
        });
    });
  });
export type NotificationSettingsInput = z.infer<typeof NotificationSettingsInput>;

// ───────── sefer kuralları (C86) ─────────

export const TRIP_RULE_METHODS = ['QR', 'PIN', 'NFC', 'GUIDE', 'MANUAL'] as const;
export type TripRuleMethod = (typeof TRIP_RULE_METHODS)[number];
export const TRIP_RULE_METHOD_LABELS: Record<TripRuleMethod, { label: string; description: string }> = {
  QR: { label: 'QR kod', description: 'Yolcu uygulamasındaki biniş kodunu araçtaki şoför okutur' },
  PIN: { label: 'PIN kodu', description: 'Uygulaması açılmayan yolcu 4 haneli kodu söyler' },
  NFC: { label: 'NFC kart', description: 'Yolcu kartını araçtaki okuyucuya dokundurur' },
  GUIDE: { label: 'Rehber onayı', description: 'Okul servisinde rehber personel bindi olarak işaretler' },
  MANUAL: { label: 'Şoför onayı', description: 'Şoför yolcuyu listeden elle işaretler (denetim kaydı tutulur)' },
};
/** Yöntemin görünmesi için gereken modül. */
export const TRIP_RULE_METHOD_FLAGS: Partial<Record<TripRuleMethod, Module>> = { NFC: 'NFC', GUIDE: 'SCHOOL' };
export const AUTO_START_OPTIONS = [0, 5, 10, 15, 30] as const;

export interface TripRulesSettings {
  delayWarnMin: number;
  delayCritMin: number;
  driverWaitMin: number;
  gpsLostMin: number;
  autoStartWindowMin: number;
  lowOccupancyPct: number;
  approachRadiusM: number;
  driverArrivalM: number;
  atStopRadiusM: number;
  boardingMethods: TripRuleMethod[];
  methods: { method: TripRuleMethod; label: string; description: string }[];
}

export const TripRulesInput = z
  .object({
    delayWarnMin: z.number().int().min(1, 'Sarı eşik en az 1 dk olmalı').max(60, 'Sarı eşik en fazla 60 dk olabilir'),
    delayCritMin: z
      .number()
      .int()
      .min(2, 'Kırmızı eşik en az 2 dk olmalı')
      .max(120, 'Kırmızı eşik en fazla 120 dk olabilir'),
    driverWaitMin: z.number().int().min(0).max(10, 'Durakta bekleme en fazla 10 dk olabilir'),
    gpsLostMin: z.number().int().min(1, 'GPS kaybı eşiği en az 1 dk olmalı').max(30, 'GPS kaybı eşiği en fazla 30 dk'),
    autoStartWindowMin: z
      .number()
      .int()
      .refine(
        (v) => (AUTO_START_OPTIONS as readonly number[]).includes(v),
        'Otomatik başlatma süresini listeden seçin',
      ),
    lowOccupancyPct: z.number().int().min(10).max(90),
    approachRadiusM: z.number().int().min(100).max(2000),
    driverArrivalM: z.number().int().min(50).max(1000),
    atStopRadiusM: z.number().int().min(20).max(300),
    boardingMethods: z.array(z.enum(TRIP_RULE_METHODS)).min(1, 'En az bir biniş doğrulama yöntemi seçin.').max(5),
  })
  .superRefine((v, ctx) => {
    if (v.delayCritMin <= v.delayWarnMin)
      ctx.addIssue({
        code: 'custom',
        path: ['delayCritMin'],
        message: 'Kırmızı eşik sarı eşikten büyük olmalı.',
      });
    if (!(v.atStopRadiusM < v.driverArrivalM && v.driverArrivalM < v.approachRadiusM))
      ctx.addIssue({
        code: 'custom',
        path: ['driverArrivalM'],
        message: 'Mesafeler küçükten büyüğe olmalı: durakta < şoför varış < yaklaşıyor.',
      });
  });
export type TripRulesInput = z.infer<typeof TripRulesInput>;

/** Sefer kuralının panelde nasıl görüneceği (C86 etki metni). */
export function tripRulesEffect(delayWarnMin: number, delayCritMin: number): string {
  return `Gecikme ${delayWarnMin} dk'yı geçince sarı, ${delayCritMin} dk'yı geçince kırmızı.`;
}

// ───────── gizlilik / KVKK (C87) ─────────

export const GPS_RETENTION_OPTIONS = [30, 90, 180, 365] as const;

export const ACCOUNT_KVKK_TYPES = ['EXPORT', 'DELETE', 'ANONYMIZE'] as const;
export type AccountKvkkType = (typeof ACCOUNT_KVKK_TYPES)[number];
export const ACCOUNT_KVKK_TYPE_LABELS: Record<AccountKvkkType, string> = {
  EXPORT: 'Veri dışa aktarma',
  DELETE: 'Silme',
  ANONYMIZE: 'Anonimleştirme',
};
export const ACCOUNT_KVKK_STATUSES = ['PENDING', 'PROCESSING', 'DONE', 'REJECTED'] as const;
export type AccountKvkkStatus = (typeof ACCOUNT_KVKK_STATUSES)[number];
export const ACCOUNT_KVKK_STATUS_LABELS: Record<AccountKvkkStatus, string> = {
  PENDING: 'Bekliyor',
  PROCESSING: 'Hazırlanıyor',
  DONE: 'Tamamlandı',
  REJECTED: 'Reddedildi',
};

export interface PrivacySettings {
  gpsRetentionDays: number;
  anonymizeAfterRetention: boolean;
  /** şoför numarası yolcuya her zaman maskeli (değiştirilemez) */
  maskDriverPhone: true;
  updatedAt: string | null;
}

export const PrivacySettingsInput = z.object({
  gpsRetentionDays: z
    .number()
    .int()
    .refine(
      (v) => (GPS_RETENTION_OPTIONS as readonly number[]).includes(v),
      'Saklama süresi 30, 90, 180 veya 365 gün olmalı',
    ),
  anonymizeAfterRetention: z.boolean(),
});
export type PrivacySettingsInput = z.infer<typeof PrivacySettingsInput>;

export interface AccountKvkkRow {
  id: string;
  type: AccountKvkkType;
  status: AccountKvkkStatus;
  /** KVKK: kişi adı kısaltılmış ("Emrah U."), firma talebinde "Firma verileri" */
  subject: string;
  requestedBy: string | null;
  reason: string | null;
  createdAt: string;
  completedAt: string | null;
  downloadUrl: string | null;
}

export const AccountKvkkQuery = z.object({
  type: z.enum(ACCOUNT_KVKK_TYPES).optional(),
  status: z.enum(ACCOUNT_KVKK_STATUSES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type AccountKvkkQuery = z.infer<typeof AccountKvkkQuery>;

export const AccountKvkkExportInput = z.object({
  reason: z.string().trim().min(3, 'Talebin sebebini kısaca yazın').max(300, 'Sebep en fazla 300 karakter olabilir'),
});
export type AccountKvkkExportInput = z.infer<typeof AccountKvkkExportInput>;

// ───────── saklanan ek ayarlar (CompanySettings.notificationConfig) ─────────

export interface StoredSettingsConfig {
  notifications?: {
    events?: Partial<
      Record<SettingsEvent, { enabled: boolean; recipients: SettingsRecipient[]; channels: SettingsChannel[] }>
    >;
    approachMin?: number;
    delayNotifyMin?: number;
    docExpiryDays?: number;
    dailyDigest?: { enabled: boolean; time: string };
    smsFallback?: boolean;
    updatedAt?: string;
  };
  profile?: { taxOffice?: string | null; logoFileId?: string | null };
  tripRules?: { autoStartWindowMin?: number };
  privacy?: { anonymizeAfterRetention?: boolean; updatedAt?: string };
}

export const DEFAULT_NOTIFICATION_SETTINGS = {
  approachMin: 3,
  delayNotifyMin: 10,
  docExpiryDays: 30,
  dailyDigest: { enabled: true, time: '18:00' },
  smsFallback: true,
} as const;
export const DEFAULT_AUTO_START_MIN = 10;

/** JSON kolonunu güvenle okur (eksik/yanlış alanlar yok sayılır). */
export function parseSettingsConfig(json: unknown): StoredSettingsConfig {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return {};
  const o = json as Record<string, unknown>;
  const obj = (v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
  return {
    notifications: obj(o.notifications) as StoredSettingsConfig['notifications'],
    profile: obj(o.profile) as StoredSettingsConfig['profile'],
    tripRules: obj(o.tripRules) as StoredSettingsConfig['tripRules'],
    privacy: obj(o.privacy) as StoredSettingsConfig['privacy'],
  };
}

/** Bayrağı kapalı modül listesinde gizlenir mi (Module[] dışarıdan). */
export function moduleVisible(flag: Module | undefined, flags: Partial<Record<Module, boolean>>): boolean {
  return !flag || !!flags[flag];
}
